import React, { useState, useMemo, useCallback, useEffect } from 'react';

import { resetPassword } from 'aws-amplify/auth';
import { Trans, useTranslation } from 'react-i18next';

import { ReactComponent as Betterflies4 } from '../../../../assets/icons/v3/betterflies-4.svg';
import { ReactComponent as TrashNew } from '../../../../assets/icons/v3/trash-new.svg';
import BdsInput from '../../../../shared/components/v3/BdsInput/BdsInput';
import BdsButton from '../../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import BdsTextButton from '../../../../shared/components/v3/Buttons/BdsTextButton/BdsTextButton';
import Toast from '../../../../shared/components/v3/Toast/Toast';
import { ACCESS_INSTRUCTIONS_PAGE, MODE_CONFIG, SEND_CODE_COUNTDOWN } from '../../../../shared/constants/auth';
import { ARROW_LEFT_ICON } from '../../../../shared/constants/images';
import {
  CLICK_ACTION,
  CONTINUE_CODE_VERIFICATION,
  RESEND_CODE_VERIFICATION,
  SECTION_CREATE_ACCOUNT,
  SECTION_FORGOT_PASSWORD
} from '../../../../shared/constants/tagManager';
import { GENERIC_ERROR_TOAST } from '../../../../shared/constants/toast';
import Users from '../../../../shared/services/api/users/users';
import TagManagerService from '../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_AUTH } from '../../../../shared/translations/auth';
import { GenericVoidFunction } from '../../../../shared/types/genericTypes';

import './VerificationCode.scss';

type CodeType = 'create-account' | 'password-reset';

interface VerificationCodeProps {
  codeType: CodeType;
  email: string;
  loading?: boolean;
  hasError?: boolean;
  handleNext: (verificationCode: string) => void;
  handleGoBack: GenericVoidFunction;
}

const VerificationCode = ({ codeType, email, loading, hasError, handleNext, handleGoBack }: VerificationCodeProps) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [counter, setCounter] = useState<number>(SEND_CODE_COUNTDOWN);
  const [resetInput, setResetInput] = useState(false);
  const [loadingResend, setLoadingResend] = useState(false);
  const [openToast, setOpenToast] = useState(false);

  const config = useMemo(() => MODE_CONFIG[codeType], [codeType]);

  const codeFormatter = useCallback(
    (value: string): string => {
      const filtered = value
        .split('')
        .filter((char) => config.pattern.test(char))
        .join('');
      return filtered.slice(0, config.length);
    },
    [config]
  );

  const clearCode = () => {
    setCode('');
    setResetInput(true);
    setTimeout(() => setResetInput(false), 0);
  };

  const handleCodeChange = (value: string) => {
    setCode(value);
  };

  const validateCode = useCallback(
    (value: string | null): string | null => {
      if (!value) return null;
      if (value.length !== config.length || !config.pattern.test(value)) {
        return t(MODE_CONFIG.errorMessage);
      }
      return null;
    },
    [config, t]
  );

  const isCodeValid = useCallback((): boolean => {
    return code.length === config.length && config.pattern.test(code);
  }, [code, config]);

  const handleNextStep = () => {
    if (!isCodeValid()) return;
    const gtmParams = codeType === 'create-account' ? SECTION_CREATE_ACCOUNT : SECTION_FORGOT_PASSWORD;
    TagManagerService.pushEvent(CLICK_ACTION, CONTINUE_CODE_VERIFICATION, gtmParams);
    handleNext(code);
  };

  const redirectToHelpCenter = () => {
    window.open(ACCESS_INSTRUCTIONS_PAGE, '_blank', 'noopener');
  };

  const handleResendCode = useCallback(async () => {
    if (counter > 0) return;
    try {
      setLoadingResend(true);
      setCounter(SEND_CODE_COUNTDOWN);
      if (codeType === 'password-reset') {
        TagManagerService.pushEvent(CLICK_ACTION, RESEND_CODE_VERIFICATION, SECTION_FORGOT_PASSWORD);
        await resetPassword({ username: email });
      } else if (codeType === 'create-account') {
        TagManagerService.pushEvent(CLICK_ACTION, RESEND_CODE_VERIFICATION, SECTION_CREATE_ACCOUNT);
        await Users.resendCode(email);
      }

      setCode('');
      setResetInput(true);
      setTimeout(() => setResetInput(false), 0);
    } catch {
      setOpenToast(true);
    } finally {
      setLoadingResend(false);
    }
  }, [counter, email]);

  useEffect(() => {
    let isMounted = true;
    if (counter > 0 && isMounted) {
      setTimeout(() => isMounted && setCounter(counter - 1), 1000);
    }

    return () => {
      isMounted = false;
    };
  }, [counter]);

  const getButtonText = () => {
    if (isCodeValid()) {
      return t(TRANSLATIONS_AUTH.verificationCode.buttons.continue);
    }
    if (counter > 0) {
      const minutes = Math.floor(counter / 60);
      const seconds = counter % 60;
      return t(TRANSLATIONS_AUTH.verificationCode.buttons.disabled, { param1: minutes, param2: seconds });
    }
    return t(TRANSLATIONS_AUTH.verificationCode.buttons.enabled);
  };

  useEffect(() => {
    if (hasError) {
      clearCode();
    }
  }, [hasError]);

  const isButtonDisabled = !isCodeValid() && counter > 0;

  return (
    <div className="auth-card-container" data-experience="new">
      <div className="auth-card-go-back">
        <BdsTextButton action={() => handleGoBack(-1)} iconLeft={ARROW_LEFT_ICON}>
          {t(TRANSLATIONS_AUTH.goBack)}
        </BdsTextButton>
      </div>
      <div className="vc-betterfly-image">
        <Betterflies4 className="image" />
      </div>
      <div className="verification-code-container flex flex-col gap-11">
        <div className="flex flex-col gap-6">
          <div className="vc-title">{t(TRANSLATIONS_AUTH.verificationCode.title)}</div>
          <div className="vc-description">{t(TRANSLATIONS_AUTH.verificationCode.description)}</div>
        </div>
        <BdsInput
          hintText={t(TRANSLATIONS_AUTH.verificationCode.codeInput.placeholder)}
          initValue={code}
          onChanged={handleCodeChange}
          validator={validateCode}
          inputFormatters={[codeFormatter]}
          onSubmitPressed={handleNextStep}
          onBlur={codeType === 'password-reset' ? handleNextStep : undefined}
          maxLength={config.length}
          enabled={true}
          suffixIcon={codeType === 'create-account' && <TrashNew className="trash-sufix-icon" />}
          onSuffixIconClick={clearCode}
          resetState={resetInput}
        />
        <div className="flex flex-row gap-6 items-center">
          <BdsButton
            variant="primary"
            size="l"
            action={isCodeValid() ? handleNextStep : handleResendCode}
            disabled={isButtonDisabled || loading || loadingResend}
            loading={loadingResend || loading}
          >
            {getButtonText()}
          </BdsButton>
          {codeType === 'create-account' && (
            <div className="flex flex-column help-center-link">
              <Trans
                i18nKey={TRANSLATIONS_AUTH.verificationCode.buttons.helpcenter}
                components={{
                  u: <button className="link" onClick={redirectToHelpCenter} />
                }}
              />
            </div>
          )}
        </div>
      </div>
      <Toast
        title={t(GENERIC_ERROR_TOAST.title)}
        description={t(GENERIC_ERROR_TOAST.description)}
        icon={GENERIC_ERROR_TOAST.icon}
        open={openToast}
        button={true}
        handleClose={() => setOpenToast(false)}
      />
    </div>
  );
};

export default VerificationCode;
